"use client";

import { FC, ReactNode, useRef, useMemo } from "react";
import cn from "classnames";
import {
  useIsStuck,
  useActiveSection,
  useScrollMarginTop,
  useNavHeight,
} from "./hooks";
import Link from "@/components/Link";
import { STICKY_NAV_ID } from "./constants";

export type Link = {
  title: string;
  href: string;
  dlt: string;
  dld: string;
};

export type StickyNavigationProps = {
  links: Link[];
  children?: ReactNode;
  offsetTop?: number;
  className?: string;
};

export const StickyNavigation: FC<StickyNavigationProps> = ({
  links = [],
  children,
  offsetTop = 0,
  className,
  ...props
}) => {
  const navRef = useRef<HTMLDivElement>(null);
  const stickyNavigationHeight = useNavHeight(navRef, 52);

  const isStuck = useIsStuck(navRef, offsetTop);
  const sectionIds = useMemo(
    () => links.map((link) => link.href.replace("#", "")),
    [links],
  );
  const activeSection = useActiveSection(
    sectionIds,
    offsetTop + stickyNavigationHeight,
  );

  // Apply scroll-margin-top to all observed sections
  useScrollMarginTop(sectionIds, offsetTop, stickyNavigationHeight);

  return (
    <div
      {...props}
      id={STICKY_NAV_ID}
      className={cn(className, "shadow-[0_1px_2px_0px_rgba(0,0,0,0.3)]")}
    >
      <div
        ref={navRef}
        style={{ top: `${offsetTop}px` }}
        className="swdc-container sticky z-10 mx-auto hidden min-h-13 items-center justify-between py-2 lg:flex"
      >
        <nav className="flex gap-8">
          {links.map((link) => (
            <Link
              key={`${link.title}:${link.href}`}
              href={link.href}
              className={cn(
                "text-swdc-black-600 hover:border-swdc-silver-400 active:text-swdc-blue-500 border-b-2 py-2 text-base leading-5 font-medium transition-colors duration-200",
                activeSection === link.href.replace("#", "") && isStuck
                  ? "border-swdc-silver-400 border-b-3 hover:border-b-2 active:border-b-2"
                  : "border-transparent",
              )}
              data-linktype={link.dlt}
              data-linkdetail={link.dld}
            >
              {link.title}
            </Link>
          ))}
        </nav>
        {children}
      </div>
    </div>
  );
};
